// views/admin/AdminRegisterView.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAdminApi, checkUserIdApi } from '@/lib/api';
import { useLoading } from '@/store/loadingStore';
import { useModal } from '@/store/modalStore';
import { AdminGrade } from '@/types';
import './AdminForm.css';

const AdminRegisterView = () => {
    const navigate = useNavigate();
    const { startLoading, stopLoading } = useLoading();
    const modal = useModal();

    // 탭 상태 추가
    const [activeTab, setActiveTab] = useState('관리자정보 등록');

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        userId: '',
        password: '',
        passwordConfirm: '',
        department: '',
        position: '',
        workScope: '',
        adminGrade: AdminGrade.일반관리자,
        memo: ''
    });

    // 에러 상태
    const [errors, setErrors] = useState<Record<string, string>>({});

    // 아이디 중복 체크 상태
    const [isUserIdChecked, setIsUserIdChecked] = useState(false);
    const [isUserIdAvailable, setIsUserIdAvailable] = useState(false);

    // 탭 변경 핸들러
    const handleTabChange = (tab: string) => {
        if (tab === '공지사항') {
            navigate('/notice');
            return;
        }
        setActiveTab(tab);
        // 다른 탭 기능은 추후 구현
    };

    // 입력값 변경 핸들러
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // 아이디 변경 시 중복체크 리셋
        if (name === 'userId') {
            setIsUserIdChecked(false);
            setIsUserIdAvailable(false);
        }

        // 에러 메시지 제거
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // 아이디 중복 체크
    const checkUserId = async () => {
        if (!formData.userId) {
            modal.alert('확인', '아이디를 입력해주세요.');
            return;
        }

        if (formData.userId.length < 4) {
            modal.alert('확인', '아이디는 4자 이상이어야 합니다.');
            return;
        }

        try {
            startLoading('중복 확인 중...');
            const response = await checkUserIdApi(formData.userId);

            if (response.duplicate) {
                modal.alert('중복 확인', '이미 사용중인 아이디입니다.');
                setIsUserIdAvailable(false);
            } else {
                modal.alert('중복 확인', '사용 가능한 아이디입니다.');
                setIsUserIdAvailable(true);
            }
            setIsUserIdChecked(true);
        } catch (error) {
            modal.alert('오류', '아이디 중복 체크 중 오류가 발생했습니다.');
        } finally {
            stopLoading();
        }
    };

    // 유효성 검사
    const validate = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) newErrors.name = '이름을 입력해주세요.';
        if (!formData.userId) newErrors.userId = '아이디를 입력해주세요.';
        if (!isUserIdChecked || !isUserIdAvailable) newErrors.userId = '아이디 중복체크를 해주세요.';
        if (!formData.password) newErrors.password = '비밀번호를 입력해주세요.';
        if (formData.password.length < 8) newErrors.password = '비밀번호는 8자 이상이어야 합니다.';
        if (!formData.passwordConfirm) newErrors.passwordConfirm = '비밀번호 확인을 입력해주세요.';
        if (formData.password !== formData.passwordConfirm) newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';

        return newErrors;
    };

    // 폼 제출
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            startLoading('관리자 등록 중...');
            await createAdminApi(formData);
            modal.alert('등록 완료', '관리자가 성공적으로 등록되었습니다.');
            navigate('/admin');
        } catch (error: any) {
            modal.alert('등록 실패', error.message || '등록 중 오류가 발생했습니다.');
        } finally {
            stopLoading();
        }
    };

    // 취소
    const handleCancel = () => {
        modal.confirm(
            '등록 취소',
            '작성중인 내용이 모두 사라집니다. 취소하시겠습니까?',
            () => navigate('/admin')
        );
    };

    return (
        <div className="device-list-container">
            {/* 탭 버튼 - StatView와 동일한 구조 */}
            <div className="tab-buttons">
                <div className="" style={{ float: "left" }}>
                    <button
                        className={`tab-button ${activeTab === '공지사항' ? 'active' : ''}`}
                        onClick={() => handleTabChange('공지사항')}
                    >
                        공지사항
                    </button>
                    <button
                        className={`tab-button ${activeTab === '관리자정보 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('관리자정보 등록')}
                    >
                        관리자정보 등록
                    </button>
                    <button
                        className={`tab-button ${activeTab === '로고 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('로고 등록')}
                    >
                        로고 등록
                    </button>
                </div>
            </div>

            {/* dlv_dv01 박스 - StatView와 동일한 구조 */}
            <div className="dlv_dv01">
                <div className="env-content">
                    {/* 헤더 */}
                    <div className="content-header">
                        <h1 className="page-title">운영자 등록</h1>
                    </div>

                    {/* 폼 영역 */}
                    <form onSubmit={handleSubmit}>
                        <table className="form-table">
                            <tbody>
                            {/* 이름 */}
                            <tr>
                                <td className="label-cell">이름 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="이름을 입력하세요"
                                    />
                                    {errors.name && <span className="error-message">{errors.name}</span>}
                                </td>
                            </tr>

                            {/* 연락처 */}
                            <tr>
                                <td className="label-cell">연락처</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="contact"
                                        value={formData.contact}
                                        onChange={handleChange}
                                        className="form-input small"
                                        placeholder="010-0000-0000"
                                    />
                                </td>
                            </tr>

                            {/* 아이디 */}
                            <tr>
                                <td className="label-cell">아이디 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            name="userId"
                                            value={formData.userId}
                                            onChange={handleChange}
                                            className="form-input medium"
                                            placeholder="아이디를 입력하세요"
                                        />
                                        <button
                                            type="button"
                                            onClick={checkUserId}
                                            className="btn-check"
                                        >
                                            중복체크
                                        </button>
                                    </div>
                                    {errors.userId && <span className="error-message">{errors.userId}</span>}
                                </td>
                            </tr>

                            {/* 비밀번호 */}
                            <tr>
                                <td className="label-cell">비밀번호 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="form-input medium"
                                        placeholder="비밀번호를 입력하세요"
                                    />
                                    {errors.password && <span className="error-message">{errors.password}</span>}
                                </td>
                            </tr>

                            {/* 비밀번호 확인 */}
                            <tr>
                                <td className="label-cell">비밀번호 확인 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <input
                                        type="password"
                                        name="passwordConfirm"
                                        value={formData.passwordConfirm}
                                        onChange={handleChange}
                                        className="form-input medium"
                                        placeholder="비밀번호를 다시 입력하세요"
                                    />
                                    {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
                                </td>
                            </tr>

                            {/* 부서 */}
                            <tr>
                                <td className="label-cell">부서</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        className="form-input medium"
                                        placeholder="부서를 입력하세요"
                                    />
                                </td>
                            </tr>

                            {/* 직급 */}
                            <tr>
                                <td className="label-cell">직급</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="position"
                                        value={formData.position}
                                        onChange={handleChange}
                                        className="form-input medium"
                                        placeholder="직급을 입력하세요"
                                    />
                                </td>
                            </tr>

                            {/* 담당업무 */}
                            <tr>
                                <td className="label-cell">담당업무</td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="workScope"
                                        value={formData.workScope}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="담당업무를 입력하세요"
                                    />
                                </td>
                            </tr>

                            {/* 운영등급 */}
                            <tr>
                                <td className="label-cell">운영등급</td>
                                <td className="input-cell">
                                    <select
                                        name="adminGrade"
                                        value={formData.adminGrade}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value={AdminGrade.최고운영자}>최고운영자</option>
                                        <option value={AdminGrade.운영자}>운영자</option>
                                        <option value={AdminGrade.부운영자}>부운영자</option>
                                        <option value={AdminGrade.일반관리자}>일반관리자</option>
                                    </select>
                                </td>
                            </tr>

                            {/* 기타 */}
                            <tr>
                                <td className="label-cell">기타</td>
                                <td className="input-cell">
                                    <textarea
                                        name="memo"
                                        value={formData.memo}
                                        onChange={handleChange}
                                        className="form-textarea"
                                        placeholder="기타 메모사항을 입력하세요"
                                    />
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        {/* 버튼 영역 */}
                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="btn-cancel">
                                취소
                            </button>
                            <button type="submit" className="btn-submit">
                                등록하기
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminRegisterView;